import debug from 'debug';
import { ConditionWaiter } from '../../common/ConditionWaiter';
import { environment } from '../../environments/environment';
import { UserAccount } from '../../common/UserAccount';
import { InsufficientFundsError, MintDateUnavailable } from '../../errors';
import { UserService } from '../user/UserService';
import { PlotData, PlotsService } from './PlotsService';
import { ethers } from 'ethers';
import axios from 'axios';
// only needed for pre-sale (merkle tree stuff)
import merkleTreeEntryList from '../../assets/data/entrylist.json';
import {
  PlotsActionType,
  PlotsDispatch,
  PlotsState,
} from '../../context/plots/PlotsContext';
import {
  UserPlotsActionType,
  UserPlotsDispatch,
  UserPlotsState,
} from '../../context/plots/UserPlotsContext';
import { PlotsSaleData } from '../../common/PlotsState';
import { LoadState } from '../../common/LoadState';
import { notEligible } from '../../common/UserPlotsState';
import { noChainId } from '../../common/ProviderUtils';
import { daysToMs, minutesToMs, secondsToMs } from '../../common/NumberUtils';
import { waitForExpectedValue, waitForMs } from '../../common/WaitUtils';

type MBaasNetwork = {
  chainID?: number;
};

const enum MBaasPath {
  GetMinted = '/chains/ethereum/addresses/eth_sale/contracts/eth_sale/methods/minted',
  MainSaleMint = '/chains/ethereum/addresses/eth_sale/contracts/eth_sale/methods/mintTo',
  EntryListMint = '/chains/ethereum/addresses/eth_sale/contracts/eth_sale/methods/entryListMintTo',
  GetCurrentSale = '/chains/ethereum/addresses/eth_sale/contracts/eth_sale/methods/currentSale',
  GetNetwork = '/chains/ethereum/status',
  GetMintsFromTx = '/queries/Mint/results',
}

const enum SaleType {
  MainSale,
  Entrylist,
}

const log = debug('app:services:MBaasPlotsService');

const BaseUrl = environment.mBaasEndPoint;
const ApiKey = environment.mBaasApiKey;
const noSale = -1;
const supplyPollDelay = secondsToMs(30);
const salePollDelay = minutesToMs(10);
const pollMaxForSale = Math.floor(salePollDelay / supplyPollDelay);

function createDefaultSale(): PlotsSaleData {
  return {
    active: false,
    endtime: 1650646800,
    id: noSale,
    maxPLOTs: 0,
    maxQuantity: 0,
    mintedPLOTs: 0,
    presale: 1,
    price: 0,
    starttime: 1650560400,
    volume: 0,
    merkleRoot: null,
    startTokenIndex: 0,
  };
}

function createAxiosInstance() {
  const baseURL = `${BaseUrl}/api/v0`;
  return axios.create({
    baseURL,
    headers: {
      Authorization: `Bearer ${ApiKey}`,
      'Content-Type': 'application/json',
    },
    withCredentials: true,
    timeout: 10000,
  });
}

function formatEthersTx(txFromAPI) {
  const tx = JSON.parse(JSON.stringify(txFromAPI));
  tx.gasLimit = tx.gas;
  tx.gasPrice = ethers.BigNumber.from(tx.gasPrice);
  tx.value = ethers.BigNumber.from(tx.value);
  delete tx.gas;
  delete tx.from;
  delete tx.hash;
  return tx;
}

export class MBaasPlotsService implements PlotsService {
  private userStateWaiter = new ConditionWaiter<UserAccount>(undefined);
  private plotsStateWaiter = new ConditionWaiter<PlotsState>();
  private plotsDispatchWaiter = new ConditionWaiter<PlotsDispatch>();
  private userPlotsStateWaiter = new ConditionWaiter<UserPlotsState>();
  private userPlotsDispatchWaiter = new ConditionWaiter<UserPlotsDispatch>();
  private sale: PlotsSaleData = undefined;
  private mBaasNetwork: MBaasNetwork = undefined;
  private supplyIntervalId: any = undefined;
  private supplyPollCount = 0;

  constructor(private userService: UserService) {}

  async initSale(force = false) {
    await this.populateSale(force);
  }

  async getMinted(walletAddress: string): Promise<number> {
    try {
      const axiosInstance = createAxiosInstance();
      const reqBody = {
        args: [this.sale.id, walletAddress],
      };

      const result = await axiosInstance.post(MBaasPath.GetMinted, reqBody);
      let output = result.data.result.output;
      if (typeof output === 'string') {
        return parseInt(output, 10);
      } else if (typeof output === 'number') {
        return output;
      }
      return 0;
    } catch (err) {
      log(err);
      return 0;
    }
  }

  setUserState(state: UserAccount): void {
    this.userStateWaiter.set(state);
    if (!state.loggedIn) {
      this.userPlotsDispatchWaiter.wait().then((dispatch) =>
        dispatch({
          type: UserPlotsActionType.Clear,
        })
      );
    }
  }
  setPlotsState(state: PlotsState): void {
    this.plotsStateWaiter.set(state);
  }
  setPlotsDispatch(dispatch: PlotsDispatch): void {
    this.plotsDispatchWaiter.set(dispatch);
  }
  setUserPlotsState(state: UserPlotsState): void {
    this.userPlotsStateWaiter.set(state);
  }
  setUserPlotsDispatch(dispatch: UserPlotsDispatch): void {
    this.userPlotsDispatchWaiter.set(dispatch);
  }
  async fetchSaleIsActive(): Promise<boolean> {
    const sale = await this.populateSale();
    return sale.active ?? false;
  }
  async fetchMintDate(): Promise<string> {
    const sale = await this.populateSale();
    return new Date(1000 * sale.starttime).toISOString();
  }
  async fetchMintEndDate(): Promise<string> {
    const sale = await this.populateSale();
    return new Date(1000 * sale.endtime).toISOString();
  }
  async fetchPlotPrice(): Promise<string> {
    const sale = await this.populateSale();
    const bn = ethers.utils.parseUnits(sale.price.toString(), 'wei');
    return ethers.utils.formatEther(bn);
  }
  async fetchEligiblePlots(walletAddress?: string): Promise<number> {
    const sale = await this.populateSale();
    if (!walletAddress) {
      const signer = (await this.userService.getSigner()) as ethers.Signer;
      walletAddress = await signer.getAddress();
      if (!walletAddress) {
        throw new Error('Not signed in');
      }
    }

    let maxQuantityPerAccount;
    if (sale.presale || sale.id === noSale) {
      const entry = this.getMerkleTreeEntryForWallet(walletAddress);
      maxQuantityPerAccount = entry?.maxQuantity ?? 0;
    } else {
      maxQuantityPerAccount = this.sale.maxQuantity;
    }

    const dispatch = await this.userPlotsDispatchWaiter.wait();
    try {
      dispatch({
        type: UserPlotsActionType.UpdateEligiblePlotsState,
        loadState: LoadState.Loading,
      });
      let result: number;
      if (sale.id === noSale) {
        result = 0;
      } else {
        result = await this.getMinted(walletAddress);
      }
      const userMintedPlots = result;
      const maxPlots = Math.max(sale.maxQuantity, maxQuantityPerAccount);
      const eligible = maxPlots - userMintedPlots;
      dispatch({
        type: UserPlotsActionType.UpdateEligiblePlots,
        eligiblePlots:
          eligible === 0 && userMintedPlots === 0 ? notEligible : eligible,
        maxPlots,
      });
      return eligible;
    } catch (e) {
      dispatch({
        type: UserPlotsActionType.UpdateEligiblePlotsState,
        loadState: LoadState.Errored,
        loadError: e.message,
      });
      throw e;
    }
  }
  async fetchPlots(walletAddress?: string): Promise<PlotData[]> {
    if (!walletAddress) {
      if (!this.userState.loggedIn) {
        throw new Error('Not logged in');
      }
      if (!this.userState.address) {
        throw new Error('No address available');
      }
      walletAddress = this.userState.address;
    }
    throw new Error('Method not implemented.'); // ignore for pre-sale?
  }
  async fetchRemainingSupply(): Promise<number> {
    // total supply - minted token (for all)
    const sale = await this.populateSale();
    return sale.maxPLOTs - sale.mintedPLOTs;
  }

  private async fetchTokenMintedWithTxHash(txHash: string) {
    const instance = createAxiosInstance();
    const result = await instance.get(MBaasPath.GetMintsFromTx, {
      params: {
        tx_hash: txHash,
        limit: 50,
      },
    });
    log(result);
    const rows = result.data.result.rows;
    if (rows?.length == 0) {
      return [];
    }

    let tokenIds: string[] = [];
    rows.forEach((row) => {
      tokenIds.push(row.token_id);
    });
    return tokenIds;
  }

  private waitForFetchTokenMintedWithTxHash(
    txHash: string,
    expectedAmount: number
  ) {
    const { promise } = waitForExpectedValue(
      () => this.fetchTokenMintedWithTxHash(txHash),
      {
        retryDelay: 1000,
        maxRetries: 600,
        condition: (mintedTokenIds) => mintedTokenIds.length >= expectedAmount,
        retryOnError: false,
      }
    );
    return promise;
  }

  // only needed for pre-sale (entryList wallet stuff)
  private getMerkleTreeEntryForWallet(wallet: string) {
    return merkleTreeEntryList.entryList.find(
      (entry) => entry.activeAccount.toLowerCase() === wallet.toLowerCase()
    );
  }

  async mintPlots(amount: number): Promise<PlotData[]> {
    const sale = await this.populateSale();
    const isMerkleRootZero = ethers.BigNumber.from(sale.merkleRoot).eq(
      ethers.BigNumber.from('0x0')
    );
    const maxQuantity = sale.maxQuantity;
    const presale = sale.presale;
    if (maxQuantity > 0 && isMerkleRootZero && presale === 0) {
      log('mintTo (main sale)');
      this.stopSalePoll();
      return this.saleMintPlots(amount, SaleType.MainSale);
    } else if (maxQuantity === 0 && !isMerkleRootZero && presale > 0) {
      log('Entrylist mintTo');
      this.stopSalePoll();
      return this.saleMintPlots(amount, SaleType.Entrylist);
    } else {
      throw new Error('Sale misconfigured!');
    }
  }

  private async saleMintPlots(
    amount: number,
    saleType: SaleType
  ): Promise<PlotData[]> {
    const signer = (await this.userService.getSigner()) as ethers.Signer;
    const activeAccount = await signer.getAddress();
    let entry = null;
    if (saleType === SaleType.Entrylist) {
      entry = this.getMerkleTreeEntryForWallet(activeAccount);
      if (!entry) {
        throw new Error('Not in entrylist');
      }
    }

    const axiosInstance = createAxiosInstance();

    try {
      const args =
        saleType === SaleType.Entrylist
          ? [entry.proof, entry.maxQuantity, activeAccount, amount]
          : [activeAccount, amount];
      const reqBody = {
        args,
        from: activeAccount,
        signer: activeAccount,
        value: ethers.BigNumber.from(
          `${Number(this.sale.price) * Number(amount)}`
        ).toString(),
      };

      const path =
        saleType === SaleType.Entrylist
          ? MBaasPath.EntryListMint
          : MBaasPath.MainSaleMint;
      const {
        data: {
          result: { tx, submitted },
        },
      } = await axiosInstance.post(path, reqBody);

      // this should always be not submitted since we havent signed.
      if (!submitted) {
        // Format the transaction so that ethers.js can sign it
        const ethersTx = formatEthersTx(tx);
        // Submit the transaction to the blockchain
        const txResponse = await signer.sendTransaction(ethersTx);
        const txHash = txResponse.hash;
        const txReceipt = await this.waitForTransactionReceipt(txHash);
        log('txReceipt: ', txReceipt);
        await waitForMs(1000); // time buffer to wait for multibaas event data to be ready
        const tokenIds = await this.waitForFetchTokenMintedWithTxHash(
          txReceipt.transactionHash,
          amount
        );
        let retval = [];
        tokenIds.forEach((tokenId) => {
          const tokenIdBN = ethers.BigNumber.from(tokenId);
          retval.push({
            id: tokenId,
            hexId: tokenIdBN.toHexString(),
          });
        });

        log('retval: ', retval);
        return retval;
      }
    } catch (err) {
      log(err);
      if (err.response) {
        if (
          err.response.data?.message
            ?.toLowerCase()
            .includes('insufficient funds')
        ) {
          throw new InsufficientFundsError();
        }
        throw new Error('Unexpected response');
      } else if (err.request) {
        throw new Error('No response');
      } else {
        throw new Error('Unknown error');
      }
    }
  }

  private waitForTransactionReceipt(
    txHash: string | Promise<string>
  ): Promise<ethers.providers.TransactionReceipt> {
    const { promise, cancel } = waitForExpectedValue(
      () => this.userService.getTransactionReceipt(txHash),
      {
        retryDelay: 1000,
        maxRetries: 600,
        retryOnError: false,
      }
    );
    return promise;
  }

  private startSalePoll() {
    this.stopSalePoll();
    this.supplyIntervalId = setInterval(async () => {
      this.supplyPollCount += 1;
      let fullRefresh = false;
      if (this.supplyPollCount >= pollMaxForSale) {
        fullRefresh = true;
        this.supplyPollCount = 0;
      }
      try {
        const sale = await this.getSale();
        this.sale = sale;
        const dispatch = await this.plotsDispatchWaiter.wait();
        if (fullRefresh) {
          const network = await this.fetchMBaasNetwork();
          dispatch({
            type: PlotsActionType.UpdateState,
            plotsSaleData: sale,
            chainId: network?.chainID ?? noChainId,
          });
        } else {
          dispatch({
            type: PlotsActionType.UpdateRemainingSupply,
            maxPLOTs: sale.maxPLOTs,
            mintedPLOTs: sale.mintedPLOTs,
          });
        }
      } catch (e) {
        log('salePoll error', e);
      }
    }, supplyPollDelay);
  }

  private stopSalePoll() {
    if (this.supplyIntervalId) {
      clearInterval(this.supplyIntervalId);
    }
    this.supplyIntervalId = undefined;
  }

  private async populateSale(force = false, forceNetwork = false) {
    if (!force && this.sale) {
      return this.sale;
    }

    const network = await this.fetchMBaasNetwork(forceNetwork);

    try {
      const dispatch = await this.plotsDispatchWaiter.wait();
      dispatch({
        type: PlotsActionType.UpdateLoadState,
        loadState: LoadState.Loading,
      });
      this.sale = await this.getSale();
      // check against default sale
      // (use default sale if it's currently before default sale's start time)
      if (this.sale && Date.now() >= this.sale.endtime * 1000) {
        const defaultSale = createDefaultSale();
        if (this.sale.starttime < defaultSale.starttime) {
          // defaulting to default data
          this.sale = defaultSale;
        }
      }
      // debug
      // this.sale.endtime = Math.floor((Date.now() + daysToMs(3)) / 1000);
      dispatch({
        type: PlotsActionType.UpdateState,
        plotsSaleData: this.sale,
        chainId: network?.chainID ?? noChainId,
      });
      this.startSalePoll();
      return this.sale;
    } catch (e) {
      log('Error occurred:', e);
      const dispatch = await this.plotsDispatchWaiter.wait();

      // check against default sale
      // (use default sale if it's currently before default sale's start time)
      const defaultSale = createDefaultSale();
      if (Date.now() < defaultSale.starttime * 1000) {
        // defaulting to default data
        this.sale = defaultSale;
        dispatch({
          type: PlotsActionType.UpdateState,
          plotsSaleData: this.sale,
          chainId: network?.chainID ?? noChainId,
        });
      } else {
        dispatch({
          type: PlotsActionType.UpdateLoadState,
          loadState: LoadState.Errored,
        });
      }
    }
  }

  private async getSale(): Promise<PlotsSaleData> {
    if (!BaseUrl?.length) {
      return createDefaultSale();
    }
    const axiosInstance = createAxiosInstance();
    const reqBody = {
      args: [],
    };

    const result = await axiosInstance.post(MBaasPath.GetCurrentSale, reqBody);
    let output = result.data.result.output;
    if (output.merkleRoot) {
      output.merkleRoot = `0x${output.merkleRoot
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('')}`;
    }
    return output;
  }

  private async fetchMBaasNetwork(force = false): Promise<MBaasNetwork> {
    if (!force && this.mBaasNetwork) {
      return this.mBaasNetwork;
    }

    try {
      const axiosInstance = createAxiosInstance();
      const {
        data: { result },
      } = await axiosInstance.get(MBaasPath.GetNetwork);
      log('mbaasnetwork', result);
      this.mBaasNetwork = result;
    } catch (e) {
      log('mbaasnetwork error', e);
      this.mBaasNetwork = null;
    }
    return this.mBaasNetwork;
  }

  private get userState(): UserAccount {
    return this.userStateWaiter.get();
  }

  private get plotsState(): PlotsState {
    return this.plotsStateWaiter.get();
  }

  private get plotsDispatch(): PlotsDispatch {
    return this.plotsDispatchWaiter.get();
  }

  private get userPlotsState(): UserPlotsState {
    return this.userPlotsStateWaiter.get();
  }

  private get userPlotsDispatch(): UserPlotsDispatch {
    return this.userPlotsDispatchWaiter.get();
  }
}
